import merge from 'lodash/merge';

import type {
  AddGalleryMediaBody,
  AiAlternativeAdsResponse,
  AiAlternativeMediaResponse,
  ArchiveProductFeedBody,
  ArchiveProductsResponse,
  AssociateContentTagsBody,
  AutocompleteResponseBody,
  BrandGuidelinesRecord,
  BulkCropBody,
  BulkCropResponseBody,
  CompetitorBrandResponse,
  CompetitorSearchResponseBody,
  ContentRightsCountsResponse,
  ContentTagResponse,
  CreateContentTagBody,
  CreateGalleryBody,
  CreateGoogleAnalyticsAccountBody,
  CreateProductFeedBody,
  CreateSegmentClusterTagAssociationsBody,
  CreateSegmentClusterTagBody,
  CsvParams,
  CsvResponse,
  DeleteAiAlternativeAdsBody,
  DeleteAiAlternativeMediaBody,
  GalleryResponse,
  GalleryTagsResponse,
  GetAiAlternativesParams,
  GetGalleriesParams,
  GetGalleryMediaParams,
  GetGalleryMediaV2Params,
  GetGalleryTagsParams,
  GetProductFeedsParams,
  GetTimeSeriesInstagramStoryParams,
  GetUtmPresetValuesParams,
  GoogleAnalyticsAccountResponseBody,
  ListAiGenerationBatchesParams,
  ListAiGenerationBatchesResponse,
  AiAssetUploadUrlResponse,
  AiProject,
  AiProjectAsset,
  AiProjectAssetsResponse,
  AiProjectChatMessage,
  AiProjectDetailResponse,
  AiProjectMessageFeedbackBody,
  AiProjectMessagesResponse,
  AiProjectRetryResponse,
  AiProjectsListResponse,
  AiProjectTurnBody,
  AiProjectTurnResponse,
  CreateAiAssetUploadUrlBody,
  CreateAiProjectBody,
  GetAiAssetsRequestParams,
  GetAiProjectAssetsParams,
  GetAiProjectMessagesParams,
  GetAiProjectsParams,
  UpdateAiProjectAssetBody,
  UpdateAiProjectBody,
  LooseParams,
  MediaDetailResponse,
  MediaLinkRecord,
  MediaPredictionFiles,
  MediaPredictionsResponse,
  MediaResponse,
  MediaTagResponse,
  MediaWithProductsResponse,
  ProductClicksResponse,
  ProductFeedResponse,
  ProductFeedsResponse,
  ProductMedia,
  ProductMediaHiddenStatusResponse,
  ProductResponse,
  ProductsAnalyticsResponse,
  ProductsResponse,
  PutMediaV2Params,
  SearchGalleriesBody,
  SegmentClusterTagResponse,
  UpdateContentTagBody,
  UpdateGalleryBody,
  UpdateBrandGuidelinesBody,
  UpdateGalleryOrderBody,
  UpdateMediaLinksBody,
  UrlMetadataResponse,
  UtmDecodeBody,
  UtmDecodeResponse,
  UtmEncodeBody,
  UtmEncodeResponse,
  UtmPresetValueResponseBody,
  UtmSettingsBody,
} from '@/apis/library.types';
import type {
  CampaignCreatorFiltersOptions,
  MediaFilters,
  MediaV2ListResponse,
} from '@/apis/queries/media-list.types';
import type { HttpResponse } from '@/types/http';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.libraryApiUrl, {
  camelizeKeys: true,
});
// instance for utm data, some keys and value comes from user input
export const utmHttp = createDashHttpClient(env.libraryApiUrl, {
  camelizeKeys: false,
  decamelizeRequests: false,
});

type BrandIdArg = { brandId: number | string };
type GalleryArg = BrandIdArg & { galleryId: number | string };
type MediaArg = BrandIdArg & { mediaId: number | string };

export function getMediaV2({ brandId, mediaId }: MediaArg, config: HttpRequestConfig = {}) {
  return http.get<MediaResponse>(`/brands/${brandId}/media/v2/${mediaId}`, config);
}

export type VideoGenerationUsage = {
  canAccessVideoGeneration: boolean;
  limit: number;
  used: number;
  expiresAt: string;
};

export async function getVideoGenerationUsage(
  { brandId }: BrandIdArg,
  config: HttpRequestConfig = {},
) {
  return http.get<VideoGenerationUsage>(`/brands/${brandId}/video_generation/usage`, config);
}

export function getGalleriesCSV(
  { brandId, ...params }: BrandIdArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(`/brands/${brandId}/galleries/csv`, merge({ params }, config));
}

export function getStoryMetricsCSV(
  { brandId, ...params }: BrandIdArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.post<CsvResponse>(
    `/brands/${brandId}/galleries/timeseries_metrics/csv`,
    {},
    merge({ params }, config),
  );
}

export function getBoardsMediaCSV(
  { brandId, ...params }: BrandIdArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(`/brands/${brandId}/galleries/media/csv`, merge({ params }, config));
}

export function getBoardMediaCSV(
  { brandId, galleryId, ...params }: GalleryArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/${galleryId}/media/csv`,
    merge({ params }, config),
  );
}

export function getGalleryMediaProductsCSV(
  { brandId, galleryId, ...params }: GalleryArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/${galleryId}/gallery_media_products/csv`,
    merge({ params }, config),
  );
}

export function getGalleryStatsCSV(
  { brandId, galleryId, ...params }: GalleryArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/${galleryId}/stats/csv`,
    merge({ params }, config),
  );
}

export function getGalleryUgcAsAltImageCSV(
  { brandId, galleryId, ...params }: GalleryArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/${galleryId}/ugc_as_alt_image/csv`,
    merge({ params }, config),
  );
}

export function getLikeShopEmailSubmissionsCSV(
  { brandId, ...params }: BrandIdArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/likeshop/email_submissions/csv`,
    merge({ params }, config),
  );
}

export function getTikTokLikeShopEmailSubmissionsCSV(
  { brandId, ...params }: BrandIdArg & CsvParams,
  config: HttpRequestConfig = {},
) {
  return http.get<CsvResponse>(
    `/brands/${brandId}/galleries/tiktok_likeshop/email_submissions/csv`,
    merge({ params }, config),
  );
}

export function getGallery({ brandId, galleryId }: GalleryArg, config: HttpRequestConfig = {}) {
  return http.get<GalleryResponse>(`/brands/${brandId}/galleries/${galleryId}`, config);
}

export function listAiGenerationBatches(
  {
    brandId,
    galleryId,
    status,
    sortDirection,
    limit,
    offset,
  }: BrandIdArg & ListAiGenerationBatchesParams,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {
    galleryId,
    ...(status && { status }),
    ...(sortDirection && { sortDirection }),
    ...(limit != null && { limit }),
    ...(offset != null && { offset }),
  };
  return http.get<ListAiGenerationBatchesResponse>(
    `/brands/${brandId}/ai_generation_batches`,
    merge({ params }, config),
  );
}

export async function createGallery(
  {
    brandId,
    name,
    description,
    tags,
    galleryType,
    duplicate,
    duplicateFilter,
  }: BrandIdArg & CreateGalleryBody,
  config: HttpRequestConfig = {},
) {
  return http.post<GalleryResponse>(
    `/brands/${brandId}/galleries`,
    { name, description, tags, galleryType, duplicate, duplicateFilter },
    config,
  );
}

export async function updateGallery(
  {
    brandId,
    galleryId,
    name,
    description,
    tags,
    spiritGalleryEnabled,
    settings,
  }: GalleryArg & UpdateGalleryBody,
  config: HttpRequestConfig = {},
) {
  return http.patch<GalleryResponse>(
    `/brands/${brandId}/galleries/${galleryId}`,
    { name, description, tags, spirit_gallery_enabled: spiritGalleryEnabled, settings },
    config,
  );
}

export async function deleteGallery(
  { brandId, galleryId }: GalleryArg,
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/galleries/${galleryId}`, config);
}

export function getGalleries(
  {
    brandId,
    url,
    type,
    startDate,
    endDate,
    tags,
    tagsFilterType,
    search,
    sort,
    limit,
    summary,
    types,
    minSize,
    live,
    spiritGalleryEnabled,
    ids,
  }: BrandIdArg & GetGalleriesParams,
  config: HttpRequestConfig = {},
) {
  const params = {
    type,
    startDate,
    endDate,
    tags,
    tagsFilterType,
    search,
    sort,
    limit,
    summary,
    types,
    minSize,
    live,
    spiritGalleryEnabled,
    ids,
  };
  return http.get<GalleryResponse[]>(
    url ?? `/brands/${brandId}/galleries`,
    merge({ params: url ? null : params }, config),
  );
}

export function getFullGalleryTagList(
  { brandId, activeOnly, galleryType, sort }: BrandIdArg & GetGalleryTagsParams,
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = { activeOnly, galleryType };
  if (sort) {
    params.sort = sort;
  }
  return http.get<GalleryTagsResponse[]>(
    `/brands/${brandId}/gallery_tags`,
    merge({ params }, config),
  );
}

export function getGalleryMedia(
  { brandId, galleryId, url, ...other }: GalleryArg & GetGalleryMediaParams,
  config: HttpRequestConfig = {},
) {
  const params = merge({}, other);
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get(`/brands/${brandId}/galleries/${galleryId}/media`, merge({ params }, config));
}

export function addGalleryMedia(
  { brandId, galleryId, mediaIds }: GalleryArg & AddGalleryMediaBody,
  config: HttpRequestConfig = {},
) {
  return http.post(`/brands/${brandId}/galleries/${galleryId}/media`, { mediaIds }, config);
}

export function removeGalleryMediaBulk(
  { brandId, galleryId, mediaIds }: GalleryArg & { mediaIds?: number[] },
  config: HttpRequestConfig = {},
) {
  return http.delete(
    `/brands/${brandId}/galleries/${galleryId}/media`,
    merge(
      {
        params: {
          mediaIds: mediaIds?.join(',') ?? [],
        },
      },
      config,
    ),
  );
}

export function removeGalleryMedia(
  { brandId, galleryId, mediaId }: GalleryArg & { mediaId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/galleries/${galleryId}/media/${mediaId}`, config);
}

export function getTimeSeriesMetricsInstagramStory(
  { brandId, scale, startDate, endDate }: BrandIdArg & GetTimeSeriesInstagramStoryParams,
  config?: HttpRequestConfig,
) {
  return http.get(
    `/brands/${brandId}/timeseries_metrics/instagram_story`,
    merge({ params: { scale, startDate, endDate } }, config ?? {}),
  );
}

export function getGalleryCSV(
  {
    brandId,
    galleryId,
    sort,
    socketId,
    type,
  }: GalleryArg & { sort?: string; socketId?: string; type?: string },
  config: HttpRequestConfig = {},
) {
  return http.post<CsvResponse>(
    `/brands/${brandId}/galleries/${galleryId}/csv`,
    { sort, socketId, type },
    config,
  );
}

export function getGalleryStats(
  { brandId, galleryId, ...params }: GalleryArg & LooseParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/galleries/${galleryId}/stats`, merge({ params }, config));
}

export function getLiveGalleriesStats(
  { brandId, ...params }: BrandIdArg & LooseParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/galleries/stats`, merge({ params }, config));
}

export function updateCustomContentSettings(
  {
    brandId,
    galleryType,
    settings,
  }: BrandIdArg & { galleryType: string; settings: Record<string, unknown> },
  config: HttpRequestConfig = {},
) {
  return http.patch(
    `/brands/${brandId}/galleries/${galleryType}/custom_content_settings`,
    settings,
    config,
  );
}

/** @deprecated Use updateCustomContentSettings */
export function updateLikeshopCustomContentSettings(
  {
    brandId,
    bannerImage,
    pinnedContent,
    emailCapture,
  }: BrandIdArg & {
    bannerImage?: unknown;
    pinnedContent?: unknown;
    emailCapture?: unknown;
  },
  config: HttpRequestConfig = {},
) {
  return http.patch(
    `/brands/${brandId}/galleries/likeshop/custom_content_settings`,
    { bannerImage, pinnedContent, emailCapture },
    config,
  );
}

export async function updateGalleryOrder(
  { brandId, galleryId, changeList }: GalleryArg & { changeList: UpdateGalleryOrderBody },
  config: HttpRequestConfig = {},
) {
  return http.patch(`/brands/${brandId}/galleries/${galleryId}/order`, changeList, config);
}

export function getCompetitorTotalsByBrand(
  { brandId }: BrandIdArg,
  config: HttpRequestConfig = {},
) {
  return utmHttp.get(`/brands/${brandId}/competitors/count`, config);
}

export async function getReport360LikeshopPosts(
  { brandId, month }: BrandIdArg & { month: string },
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/reports/360/likeshop`, merge({ params: { month } }, config));
}

export async function getTrendGroups(
  { brandId, source }: BrandIdArg & { source?: string },
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/trend_groups`, merge({ params: { source } }, config));
}

export async function getTrendGroup(
  { brandId, trendGroupId }: BrandIdArg & { trendGroupId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/trend_groups/${trendGroupId}`, config);
}

export async function createTrendGroup(
  {
    brandId,
    name,
    description,
    tags,
    targetSource,
  }: BrandIdArg & {
    name: string;
    description?: string;
    tags?: string[];
    targetSource?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/trend_groups`,
    { name, description, tags, targetSource },
    config,
  );
}

export async function updateTrendGroup(
  {
    brandId,
    trendGroupId,
    name,
    description,
    tags,
  }: BrandIdArg & {
    trendGroupId: number | string;
    name?: string;
    description?: string;
    tags?: string[];
  },
  config: HttpRequestConfig = {},
) {
  return http.patch(
    `/brands/${brandId}/trend_groups/${trendGroupId}`,
    { name, description, tags },
    config,
  );
}

export async function deleteTrendGroup(
  { brandId, trendGroupId }: BrandIdArg & { trendGroupId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/trend_groups/${trendGroupId}`, config);
}

export async function addTrendGroupAccount(
  {
    brandId,
    trendGroupId,
    accountId,
  }: BrandIdArg & { trendGroupId: number | string; accountId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/brands/${brandId}/trend_groups/${trendGroupId}/accounts`,
    { accountId },
    config,
  );
}

export async function removeTrendGroupAccount(
  {
    brandId,
    trendGroupId,
    accountId,
  }: BrandIdArg & { trendGroupId: number | string; accountId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(
    `/brands/${brandId}/trend_groups/${trendGroupId}/accounts/${accountId}`,
    config,
  );
}

export async function getTrendGroupStats(
  {
    brandId,
    trendId,
    period,
    resolution,
    sourceId,
  }: BrandIdArg & {
    trendId: number | string;
    period?: string;
    resolution?: string;
    sourceId?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/trend_groups/${trendId}/stats`,
    merge({ params: { period, resolution, sourceId } }, config),
  );
}

export async function removeTrendGroupTag(
  {
    brandId,
    trendGroupId,
    tagId,
  }: BrandIdArg & { trendGroupId: number | string; tagId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/trend_groups/${trendGroupId}/tags/${tagId}`, config);
}

export async function getTrendGroupTags(
  { brandId, activeOnly }: BrandIdArg & { activeOnly?: boolean },
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/trend_group_tags`, merge({ params: { activeOnly } }, config));
}

export async function getSegmentCluster(
  {
    brandId,
    segmentClusterId,
    isUgc,
    period,
    sort,
    resolution,
  }: BrandIdArg & {
    segmentClusterId: number | string;
    isUgc?: boolean;
    period?: string;
    sort?: string;
    resolution?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/segment_clusters/${segmentClusterId}`,
    merge(
      {
        params: { isUgc, period, sort, resolution },
        camelizeKeys: true,
      },
      config,
    ),
  );
}

export async function updateSegmentCluster(
  {
    brandId,
    segmentClusterId,
    tags,
  }: BrandIdArg & { segmentClusterId: number | string; tags: number[] },
  config: HttpRequestConfig = {},
) {
  return http.patch(`/brands/${brandId}/segment_clusters/${segmentClusterId}`, { tags }, config);
}

export async function removeSegmentClusterTag(
  {
    brandId,
    clusterId,
    tagId,
  }: BrandIdArg & { clusterId: number | string; tagId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/segment_clusters/${clusterId}/tags/${tagId}`, config);
}

export async function deleteSegmentCluster(
  { brandId, clusterId }: BrandIdArg & { clusterId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/segment_clusters/${clusterId}`, config);
}

export async function getSegmentClusterTags(
  { brandId, activeOnly }: BrandIdArg & { activeOnly?: boolean },
  config: HttpRequestConfig = {},
) {
  return http.get<SegmentClusterTagResponse[]>(
    `/brands/${brandId}/segment_cluster_tags`,
    merge({ params: { activeOnly } }, config),
  );
}

export async function removeTagFromAllSegmentClusters(
  { brandId, tagId }: BrandIdArg & { tagId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/brands/${brandId}/segment_cluster_tags/${tagId}`, config);
}

export async function createSegmentClusterTag(
  { brandId, name, color }: BrandIdArg & CreateSegmentClusterTagBody,
  config: HttpRequestConfig = {},
) {
  return http.post<SegmentClusterTagResponse>(
    `/brands/${brandId}/segment_cluster_tags`,
    { name, color },
    config,
  );
}

export async function createSegmentClusterTagAssociations(
  {
    brandId,
    segmentClusterIds,
    segmentClusterTagIds,
  }: BrandIdArg & CreateSegmentClusterTagAssociationsBody,
  config: HttpRequestConfig = {},
) {
  await http.post(
    `/brands/${brandId}/segment_cluster_tag_associations`,
    { segmentClusterIds, segmentClusterTagIds },
    config,
  );
}

export async function getSegmentClusterStats(
  {
    brandId,
    clusterId,
    period,
    resolution,
    sourceId,
  }: BrandIdArg & {
    clusterId: number | string;
    period?: string;
    resolution?: string;
    sourceId?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/segment_clusters/${clusterId}/stats`,
    merge({ params: { period, resolution, sourceId } }, config),
  );
}

export async function getSegments(
  {
    brandId,
    source,
    sourceId,
    sort,
    period,
    resolution,
    mediaCount,
    isUgc,
    brand,
    token,
  }: BrandIdArg & {
    source?: string;
    sourceId?: string;
    sort?: string;
    period?: string;
    resolution?: string;
    mediaCount?: number;
    isUgc?: boolean;
    brand?: string;
    token?: string;
  },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/segments`,
    merge(
      {
        params: {
          source,
          sourceId,
          sort,
          period,
          resolution,
          mediaCount,
          isUgc,
          brand,
          token,
        },
      },
      config,
    ),
  );
}

export async function getSegment(
  { brandId, segmentId }: BrandIdArg & { segmentId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.get(`/brands/${brandId}/segments/${segmentId}`, config);
}

export async function getSegmentStats(
  {
    brandId,
    source,
    sourceId,
    isUgc,
  }: BrandIdArg & { source?: string; sourceId?: string; isUgc?: boolean },
  config: HttpRequestConfig = {},
) {
  return http.get(
    `/brands/${brandId}/segments/stats`,
    merge({ params: { source, sourceId, isUgc } }, config),
  );
}

export async function getContentTags(
  { organizationId, contentTagIds }: { organizationId: number | string; contentTagIds?: number[] },
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (contentTagIds?.length && contentTagIds.length > 0) {
    params.ids = contentTagIds.join(',');
  }
  return http.get<ContentTagResponse[]>(
    `/organizations/${organizationId}/content_tags`,
    merge({ params }, config),
  );
}

export async function createContentTag(
  { organizationId, name, brandIds }: { organizationId: number | string } & CreateContentTagBody,
  config: HttpRequestConfig = {},
) {
  const data: { name: string; brandIds?: number[] } = { name };
  if (brandIds?.length && brandIds.length > 0) {
    data.brandIds = brandIds;
  }
  return http.post<ContentTagResponse>(
    `/organizations/${organizationId}/content_tags`,
    data,
    config,
  );
}

export async function updateContentTag(
  {
    organizationId,
    contentTagId,
    name,
    brandIds,
  }: {
    organizationId: number | string;
    contentTagId: number | string;
  } & UpdateContentTagBody,
  config: HttpRequestConfig = {},
) {
  const data = { name, brandIds };
  return http.patch<ContentTagResponse>(
    `/organizations/${organizationId}/content_tags/${contentTagId}`,
    data,
    config,
  );
}

export async function deleteContentTag(
  {
    organizationId,
    contentTagId,
  }: { organizationId: number | string; contentTagId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete(`/organizations/${organizationId}/content_tags/${contentTagId}`, config);
}

export async function deleteContentTagBrand(
  {
    organizationId,
    contentTagId,
    brandId,
  }: {
    organizationId: number | string;
    contentTagId: number | string;
    brandId: number | string;
  },
  config: HttpRequestConfig = {},
) {
  return http.delete(
    `/organizations/${organizationId}/content_tags/${contentTagId}/brands/${brandId}`,
    config,
  );
}

export async function associateContentTagsToBrandMedia(
  {
    organizationId,
    contentTagIds,
    brandMediaIds,
    createMissingAssociations,
  }: { organizationId: number | string } & AssociateContentTagsBody,
  config: HttpRequestConfig = {},
) {
  return http.post(
    `/organizations/${organizationId}/brand_media_content_tags`,
    { contentTagIds, brandMediaIds, createMissingAssociations },
    config,
  );
}

export async function removeContentTagFromBrandMedia(
  {
    organizationId,
    contentTagId,
    brandMediaId,
  }: {
    organizationId: number | string;
    contentTagId: number | string;
    brandMediaId: number | string;
  },
  config: HttpRequestConfig = {},
) {
  return http.delete(
    `/organizations/${organizationId}/content_tags/${contentTagId}/brand_media/${brandMediaId}`,
    config,
  );
}

export async function getPredictions(
  { brandId, files }: BrandIdArg & { files: MediaPredictionFiles },
  config: HttpRequestConfig = {},
) {
  return http.post<MediaPredictionsResponse>(`/brands/${brandId}/media_predictions`, files, config);
}

export function getGalleriesForBrands(
  { search, types, brandIds, limit }: SearchGalleriesBody,
  config: HttpRequestConfig = {},
) {
  return http.post(
    '/brands/galleries/search',
    { search, types, brand_ids: brandIds, limit },
    config,
  );
}

export function packageBulkCroppedImages(
  { imageUrl, filename, crops }: BulkCropBody,
  config: HttpRequestConfig = {},
) {
  return http.post<BulkCropResponseBody>(
    '/image_export/bulk_crop',
    { imageUrl, filename, crops },
    config,
  );
}

export function bulkSaveCroppedImages(
  { brandId, imageUrl, filename, crops }: BrandIdArg & BulkCropBody,
  config: HttpRequestConfig = {},
) {
  return http.post<BulkCropResponseBody>(
    `/brands/${brandId}/image_export/bulk_crop_save`,
    { imageUrl, filename, crops },
    config,
  );
}

export interface DocumentUploadUrlResponse {
  /** Presigned S3 URL to PUT the bytes to. Expires 30 minutes after it is issued. */
  url: string;
  /** Public CDN URL the document will be readable at. Already rewritten by the backend. */
  fileUrl: string;
}

/**
 * Presign a document upload. The document counterpart to `/media_upload_url`, which only
 * accepts images and videos.
 */
export function createDocumentUploadUrl(
  { brandId, filename, contentType }: BrandIdArg & { filename: string; contentType: string },
  config: HttpRequestConfig = {},
) {
  return http.post<DocumentUploadUrlResponse>(
    `/brands/${brandId}/document_upload_url`,
    { filename, contentType },
    config,
  );
}

export function getBrandGuidelines({ brandId }: BrandIdArg, config: HttpRequestConfig = {}) {
  return http.get<BrandGuidelinesRecord>(`/brands/${brandId}/brand-guidelines`, config);
}

export function addBrandGuidelinesAssets(
  { brandId, files }: BrandIdArg & { files: File[] },
  config: HttpRequestConfig = {},
) {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  return http.post<BrandGuidelinesRecord>(`/brands/${brandId}/brand-guidelines/assets`, formData, {
    ...config,
    decamelizeRequests: false,
  });
}

export function updateBrandGuidelinesInstructions(
  {
    brandId,
    additionalInstructions,
  }: BrandIdArg & { additionalInstructions: UpdateBrandGuidelinesBody['additionalInstructions'] },
  config: HttpRequestConfig = {},
) {
  return http.put<BrandGuidelinesRecord>(
    `/brands/${brandId}/brand-guidelines`,
    { additionalInstructions },
    config,
  );
}

export function removeBrandGuidelinesAsset(
  { brandId, assetId }: BrandIdArg & { assetId: string },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/brand-guidelines/assets/${assetId}`, config);
}

type AiProjectArg = BrandIdArg & { projectId: number | string };
type AiProjectChatArg = AiProjectArg & { chatId: number | string };
type AiProjectMessageArg = AiProjectChatArg & { messageId: number | string };

export function getAiProjects(
  { brandId, ...params }: BrandIdArg & GetAiProjectsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<AiProjectsListResponse>(`/brands/${brandId}/ai_projects`, {
    ...config,
    params,
  });
}

export function getAiProject({ brandId, projectId }: AiProjectArg, config: HttpRequestConfig = {}) {
  return http.get<AiProjectDetailResponse>(`/brands/${brandId}/ai_projects/${projectId}`, config);
}

export function createAiProject(
  { brandId, ...body }: BrandIdArg & CreateAiProjectBody,
  config: HttpRequestConfig = {},
) {
  return http.post<AiProjectTurnResponse>(`/brands/${brandId}/ai_projects`, body, config);
}

export function updateAiProject(
  { brandId, projectId, name }: AiProjectArg & UpdateAiProjectBody,
  config: HttpRequestConfig = {},
) {
  return http.patch<AiProject>(`/brands/${brandId}/ai_projects/${projectId}`, { name }, config);
}

export function deleteAiProject(
  { brandId, projectId }: AiProjectArg,
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/ai_projects/${projectId}`, config);
}

export function getAiProjectChatMessages(
  { brandId, projectId, chatId, ...params }: AiProjectChatArg & GetAiProjectMessagesParams,
  config: HttpRequestConfig = {},
) {
  return http.get<AiProjectMessagesResponse>(
    `/brands/${brandId}/ai_projects/${projectId}/chats/${chatId}/messages`,
    { ...config, params },
  );
}

export function sendAiProjectChatTurn(
  { brandId, projectId, chatId, ...body }: AiProjectChatArg & AiProjectTurnBody,
  config: HttpRequestConfig = {},
) {
  return http.post<AiProjectTurnResponse>(
    `/brands/${brandId}/ai_projects/${projectId}/chats/${chatId}/turns`,
    body,
    config,
  );
}

export function retryAiProjectChatMessage(
  { brandId, projectId, chatId, messageId }: AiProjectMessageArg,
  config: HttpRequestConfig = {},
) {
  return http.post<AiProjectRetryResponse>(
    `/brands/${brandId}/ai_projects/${projectId}/chats/${chatId}/messages/${messageId}/retry`,
    undefined,
    config,
  );
}

export function updateAiProjectMessageFeedback(
  {
    brandId,
    projectId,
    chatId,
    messageId,
    feedback,
  }: AiProjectMessageArg & AiProjectMessageFeedbackBody,
  config: HttpRequestConfig = {},
) {
  return http.patch<AiProjectChatMessage>(
    `/brands/${brandId}/ai_projects/${projectId}/chats/${chatId}/messages/${messageId}`,
    { feedback },
    config,
  );
}

export function markAiProjectChatRead(
  { brandId, projectId, chatId }: AiProjectChatArg,
  config: HttpRequestConfig = {},
) {
  return http.post<void>(
    `/brands/${brandId}/ai_projects/${projectId}/chats/${chatId}/read`,
    undefined,
    config,
  );
}

export function getAiProjectAssets(
  { brandId, projectId, ...params }: AiProjectArg & GetAiProjectAssetsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<AiProjectAssetsResponse>(`/brands/${brandId}/ai_projects/${projectId}/assets`, {
    ...config,
    params,
  });
}

export function updateAiProjectAsset(
  {
    brandId,
    projectId,
    assetId,
    isFavourite,
  }: AiProjectArg & { assetId: number | string } & UpdateAiProjectAssetBody,
  config: HttpRequestConfig = {},
) {
  return http.patch<AiProjectAsset>(
    `/brands/${brandId}/ai_projects/${projectId}/assets/${assetId}`,
    { isFavourite },
    config,
  );
}

export function deleteAiProjectAsset(
  { brandId, projectId, assetId }: AiProjectArg & { assetId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/ai_projects/${projectId}/assets/${assetId}`, config);
}

export function getAiAssets(
  { brandId, aspectRatio, language, ...params }: BrandIdArg & GetAiAssetsRequestParams,
  config: HttpRequestConfig = {},
) {
  return http.get<AiProjectAssetsResponse>(`/brands/${brandId}/ai_assets`, {
    ...config,
    params: {
      ...params,
      // Comma-joined, not `paramsSerializer`-repeated — unjoined arrays would go out as
      // `aspect_ratio[]=` and be dropped on the floor. An absent or empty list means no filter,
      // so it's dropped entirely rather than sent as an empty value.
      ...(aspectRatio?.length ? { aspectRatio: aspectRatio.join(',') } : {}),
      ...(language?.length ? { language: language.join(',') } : {}),
    },
  });
}

/**
 * Presign a reference upload for Constellation Pro. Brand-scoped rather than
 * project-scoped: the first turn's references are uploaded before a project exists.
 */
export function createAiAssetUploadUrl(
  { brandId, ...body }: BrandIdArg & CreateAiAssetUploadUrlBody,
  config: HttpRequestConfig = {},
) {
  return http.post<AiAssetUploadUrlResponse>(
    `/brands/${brandId}/ai_assets/upload_url`,
    body,
    config,
  );
}

const api = {
  async getGalleryMediaV2(
    {
      brandId,
      galleryId,
      offset = 0,
      limit = 100,
      sort,
      withProductsOnly = false,
      startDate,
      endDate,
      excludeInstagramOwnedWithoutProducts,
      include,
      url,
    }: GalleryArg &
      Omit<GetGalleryMediaV2Params, 'limit'> & {
        excludeInstagramOwnedWithoutProducts?: boolean;
        limit?: number | null;
      },
    config: HttpRequestConfig = {},
  ) {
    const params: Record<string, unknown> = { offset, withProductsOnly };
    if (limit != null) {
      params.limit = limit;
    }
    if (include) {
      params.include = include.join(',');
    }
    if (sort) {
      params.sort = sort;
    }
    if (startDate && endDate) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (excludeInstagramOwnedWithoutProducts) {
      params.excludeInstagramOwnedWithoutProducts = excludeInstagramOwnedWithoutProducts;
    }

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.get(
      `/brands/${brandId}/galleries/${galleryId}/media/v2`,
      merge({ params }, config),
    );
  },

  async getInteractionMediaById(
    { brandId, mediaId }: MediaArg,
    config: HttpRequestConfig = {},
    params: Record<string, unknown> = {},
  ) {
    // Same v1 media detail endpoint as `getMedia`.
    const url = `/brands/${brandId}/media/${mediaId}`;
    return http.get<MediaDetailResponse>(url, merge({ params }, config));
  },

  async getMediaListV2(
    {
      brandId,
      limit,
      offset,
      sortFields,
      filters,
      url,
      includeCreator,
      includeInstagramUsers,
      campaignCreatorFiltersOptions,
    }: BrandIdArg &
      PutMediaV2Params & {
        sortFields?: (string | undefined)[];
        filters?: MediaFilters;
        includeCreator?: boolean;
        includeInstagramUsers?: boolean;
        campaignCreatorFiltersOptions?: CampaignCreatorFiltersOptions;
      },
    config?: HttpRequestConfig,
  ) {
    const params: Record<string, unknown> = { limit, offset };
    const data = {
      sortFields,
      filters,
      includeCreator,
      includeInstagramUsers,
      campaignCreatorFiltersOptions,
    };

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.put<MediaV2ListResponse>(
      `/brands/${brandId}/media/v2`,
      data,
      merge({ params }, config ?? {}),
    );
  },

  async getContentRightsCounts(
    { brandId, filters }: BrandIdArg & { filters?: unknown },
    config?: HttpRequestConfig,
  ) {
    return http.put<ContentRightsCountsResponse>(
      `/brands/${brandId}/media/v2/content_rights_counts`,
      { filters },
      config,
    );
  },

  async getMediaSearch(
    {
      limit,
      offset,
      sortFields,
      filters,
      includeCreator,
    }: {
      limit?: number;
      offset?: number;
      sortFields?: unknown;
      filters?: unknown;
      includeCreator?: boolean;
    },
    config?: HttpRequestConfig,
  ) {
    const data = { sortFields, filters, includeCreator, limit, offset };
    return http.post(`/media/search`, data, config);
  },

  /** @deprecated Use updateCustomContentSettings */
  async updateTikTokLikeShopCustomContent({ brandId, data }: BrandIdArg & { data: unknown }) {
    return http.patch(`/brands/${brandId}/galleries/tiktok_likeshop/custom_content_settings`, data);
  },

  async getUtmSettingsForTikTokLikeShop({
    brandId,
    data,
  }: BrandIdArg & { data?: HttpRequestConfig }) {
    return utmHttp.get(`/brands/${brandId}/galleries/tiktok_likeshop`, data);
  },

  async saveUtmSettingsForTikTokLikeShop({
    brandId,
    data,
    galleryId,
  }: GalleryArg & { data: unknown }) {
    return utmHttp.patch(`/brands/${brandId}/galleries/${galleryId}`, data);
  },

  async getGoogleAnalyticsAccounts({ brandId }: BrandIdArg) {
    return http.get<GoogleAnalyticsAccountResponseBody[]>(
      `/brands/${brandId}/google_analytics/accounts`,
    );
  },

  async createAnalyticsAccount({
    brandId,
    data,
  }: BrandIdArg & { data: CreateGoogleAnalyticsAccountBody }) {
    const path = `/brands/${brandId}/google_analytics/account`;
    return http.post<GoogleAnalyticsAccountResponseBody>(path, { ...data });
  },

  async getGoogleAnalyticAccountsByBrandId({ brandId }: BrandIdArg, payload?: HttpRequestConfig) {
    return http.get(`/brands/${brandId}/google_analytics/list_accounts`, payload);
  },

  async getBrandsGoogleAnalyticsAccount({ brandId }: BrandIdArg, config: HttpRequestConfig = {}) {
    return http.get<GoogleAnalyticsAccountResponseBody>(
      `/brands/${brandId}/google_analytics/account`,
      config,
    );
  },

  async getSegmentClusters(
    {
      brandId,
      tagId,
      sources,
      isUgc,
      period,
      mediaCount,
      search,
      tags,
      tagsFilterType,
      sort,
      limit,
      offset,
      summary = true,
      url,
    }: BrandIdArg & {
      tagId?: number | string;
      sources?: string[];
      isUgc?: boolean;
      period?: string;
      mediaCount?: number;
      search?: string;
      tags?: number[];
      tagsFilterType?: string;
      sort?: string;
      limit?: number;
      offset?: number;
      summary?: boolean;
      url?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const params: Record<string, unknown> = {
      tagId,
      sources,
      isUgc,
      period,
      mediaCount,
      search,
      tags,
      tagsFilterType,
      sort,
      limit,
      offset,
      summary,
    };
    Object.keys(params).forEach((key) => {
      if (params[key] === undefined) {
        delete params[key];
      }
    });
    return http.get(url || `/brands/${brandId}/segment_clusters`, {
      ...config,
      params,
    });
  },

  async getMediaSearchList({
    brandId,
    sort,
    query,
    aspectRatio,
    size,
    type,
    color,
    rightsReqd,
    dateFrom,
    dateTo,
    imageDataIncluded,
    mediaId,
    predictionModel,
    performanceFrom,
    performanceTo,
    page,
    limit,
    sources,
    nextToken,
    imageData,
    signal,
    contentTags,
  }: BrandIdArg & {
    sort?: string;
    query?: string;
    aspectRatio?: string;
    size?: string;
    type?: string;
    color?: string;
    rightsReqd?: boolean;
    dateFrom?: string;
    dateTo?: string;
    imageDataIncluded?: boolean;
    mediaId?: number | string;
    predictionModel?: string;
    performanceFrom?: number;
    performanceTo?: number;
    page?: number;
    limit?: number;
    sources?: string[];
    nextToken?: string;
    imageData?: unknown;
    signal?: AbortSignal;
    /** Tag *names*, which is what the picker's values are and what `MediaSearchBody` declares. */
    contentTags?: string[];
  }) {
    const url = `/brands/${brandId}/media_search`;
    // `clear` is deliberately not a parameter: it tells the *caller* whether to reset its own
    // list, and `MediaSearchBody` does not declare it, so sending it 400s under a strict spec.
    const data = { image_data: imageData, contentTags };
    const params = {
      image_data: imageDataIncluded,
      brand_id: brandId,
      sort,
      query,
      aspect_ratio: aspectRatio,
      size,
      type,
      color,
      rights_reqd: rightsReqd,
      date_from: dateFrom,
      date_to: dateTo,
      media_id: mediaId,
      prediction_model: predictionModel,
      performance_from: performanceFrom,
      performance_to: performanceTo,
      page,
      limit,
      sources,
      next_token: nextToken,
    };
    return http.put(url, data, { params, signal });
  },

  async getImageData(mediaId: number | string) {
    const libraryHttp = createDashHttpClient(env.libraryApiUrl);
    return libraryHttp.get(`/media/${mediaId}/image_data`);
  },

  async fetchMediaProducts({ brandId, mediaId }: MediaArg, config: HttpRequestConfig = {}) {
    return http.get<MediaWithProductsResponse>(
      `/brands/${brandId}/media/${mediaId}/media_products`,
      { ...config },
    );
  },

  // v1 media detail endpoint (used by MediaPopup). Source lookups hit a sibling
  // path; brand-scoped lookups allow on-the-fly indexing of missing media.
  async getMedia(
    {
      brandId,
      mediaId,
      source,
      insightsStart,
      insightsEnd,
    }: MediaArg & {
      source?: string;
      insightsStart?: string;
      insightsEnd?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    if (source) {
      return http.get<MediaDetailResponse>(
        `/brands/${brandId}/media/source/${mediaId}`,
        merge({ params: { sourceType: source } }, config),
      );
    }
    const params: Record<string, unknown> = { allowIndex: true };
    if (insightsStart && insightsEnd) {
      params.insightsStartDate = Temporal.PlainDate.from(insightsStart).toString();
      params.insightsEndDate = Temporal.PlainDate.from(insightsEnd).toString();
    }
    return http.get<MediaDetailResponse>(
      `/brands/${brandId}/media/${mediaId}`,
      merge({ params }, config),
    );
  },

  async getMediaV2({ brandId, mediaId }: MediaArg, config: HttpRequestConfig = {}) {
    return http.get<MediaResponse>(`/brands/${brandId}/media/v2/${mediaId}`, { ...config });
  },

  async createMedia({ brandId }: BrandIdArg, data: unknown, config: HttpRequestConfig = {}) {
    return http.post(`/brands/${brandId}/media`, data, { ...config });
  },

  async createMediaV2({ brandId }: BrandIdArg, data: unknown, config: HttpRequestConfig = {}) {
    return http.post(`/brands/${brandId}/media/v2`, data, { ...config });
  },

  async getMediaList(
    {
      brandId,
      url,
      sortFields,
      sort,
      filters,
      include,
      ids,
      limit,
      includeInstagramUsers = false,
    }: BrandIdArg & {
      url?: string;
      sortFields?: unknown;
      sort?: string;
      filters?: unknown;
      include?: unknown;
      ids?: number[];
      limit?: number;
      includeInstagramUsers?: boolean;
    },
    config: HttpRequestConfig = {},
  ) {
    const params: Record<string, unknown> = { include };
    const data = {
      sortFields,
      sort,
      filters,
      ids,
      limit,
      includeInstagramUsers,
    };

    if (url) {
      merge(params, parseQuery(url));
    }

    return http.put(`/brands/${brandId}/media/v2`, data, merge({ params }, config));
  },

  async getBrandMediaListBySource(
    {
      brandId,
      source,
      sourceIds,
    }: BrandIdArg & { source?: string; sourceIds: (number | string)[] },
    config: HttpRequestConfig = {},
  ) {
    return http.get(`/brands/${brandId}/media`, {
      ...config,
      params: { source, sourceIds: sourceIds.join(',') },
    });
  },

  async getTopPostMediaList(
    {
      brandId,
      limit,
      filters,
      sortFields,
      url,
    }: BrandIdArg & {
      limit?: number;
      filters?: unknown;
      sortFields?: unknown;
      url?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const params: Record<string, unknown> = { limit };
    const data = { sortFields, filters };

    if (url) {
      merge(params, parseQuery(url));
    }

    const response = await http.put<{ data?: unknown }>(
      `/brands/${brandId}/media/v2`,
      data,
      merge({ params }, config),
    );
    return (response.data as { data?: unknown }).data;
  },

  async getMediaListCSV(
    {
      brandId,
      source,
      filters,
      sortFields,
      sort,
      socketId,
      limit,
      fileNamePrefix,
    }: BrandIdArg & {
      source?: string;
      filters?: unknown;
      sortFields?: unknown;
      sort?: string;
      socketId?: string;
      limit?: number;
      fileNamePrefix?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const params = { brandId };
    const data = {
      source,
      filters,
      sortFields,
      sort,
      limit,
      fileNamePrefix,
      socketId,
    };
    return http.put<CsvResponse>(`/brands/${brandId}/media/v2/csv`, data, {
      ...config,
      params,
    });
  },

  async updateMediaLinks(
    { mediaId, brandId, data }: MediaArg & { data: UpdateMediaLinksBody[] },
    config: HttpRequestConfig = {},
  ): Promise<HttpResponse<MediaLinkRecord[]>> {
    return http.patch(`/brands/${brandId}/media/${mediaId}/media_products`, data, config);
  },

  async bulkDownloadMedia({
    brandId,
    mediaIds,
    socketId,
  }: BrandIdArg & { mediaIds: number[]; socketId: string }) {
    return http.post<{ taskId: number | string; message: string }>(
      `/brands/${brandId}/media/bulk_download`,
      { mediaIds, socketId },
    );
  },

  async bulkUpdateBrandMedia({
    brandId,
    mediaIds,
    muteSettings,
    socketId,
  }: BrandIdArg & {
    mediaIds: number[];
    muteSettings?: { muteAll?: boolean; muteMusic?: boolean };
    socketId?: string;
  }) {
    return http.patch<{
      updatedMediaIds?: number[];
      // Present only for a mute (muteMusic: true)
      muteResult?: {
        alreadyMutedMediaIds: number[];
        mutingMediaIds: number[];
        enqueueFailedMediaIds: number[];
      };
    }>(`/brands/${brandId}/media/bulk`, {
      mediaIds,
      muteSettings,
      socketId,
    });
  },

  async classifyAudio({ brandId, mediaId }: MediaArg) {
    return http.post<{ status: 'classified' | 'skipped' | 'failed'; hasMusic: boolean | null }>(
      `/brands/${brandId}/media/${mediaId}/classify-audio`,
    );
  },

  async dismissAudioAlert({ brandId, mediaId, kind }: MediaArg & { kind: 'mute' | 'reclassify' }) {
    return http.delete<void>(`/brands/${brandId}/media/${mediaId}/audio-alerts/${kind}`);
  },

  /**
   * Enqueue the on-demand ACRCloud song lookup. `skipped` means the backend declined —
   * ineligible, already resolved, or a scan is already in flight under another socket.
   */
  async identifySong({ brandId, mediaId, socketId }: MediaArg & { socketId: string }) {
    return http.post<{ status: 'enqueued' | 'skipped' }>(
      `/brands/${brandId}/media/${mediaId}/identify-song`,
      { socketId },
    );
  },

  async getListMediaLinks({ brandId, mediaId }: MediaArg, config: HttpRequestConfig = {}) {
    return http.get<MediaLinkRecord[]>(
      `/brands/${brandId}/media/${mediaId}/media_products`,
      config,
    );
  },

  async getSimilarMediaList(
    {
      brandId,
      mediaId,
      groupId,
      sources,
      limit,
      page,
      performanceFrom,
      performanceTo,
    }: MediaArg & {
      groupId?: number | string;
      sources?: string[];
      limit?: number;
      page?: number;
      performanceFrom?: number;
      performanceTo?: number;
    },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/media_search`;
    const params = {
      brandId,
      mediaId,
      groupId,
      sources,
      limit,
      page,
      performance_from: performanceFrom,
      performance_to: performanceTo,
    };
    return http.get(url, { ...config, params });
  },

  async getMediaSourceList({
    brandId,
    groupId,
    globalIds,
  }: BrandIdArg & { groupId?: number | string; globalIds?: string[] }) {
    const url = `/brands/${brandId}/media`;
    const params = { brandId, group_id: groupId, global_ids: globalIds };
    return http.get(url, { params });
  },

  async apiGetCompetitors({ payload }: { payload: unknown }, config: HttpRequestConfig = {}) {
    const url = '/competitors/search';
    return http.post<CompetitorSearchResponseBody>(url, payload, config);
  },

  async deleteGoogleAnalyticsAccount(brandId: number | string) {
    return http.delete(`/brands/${brandId}/google_analytics/account`);
  },

  async getAutocomplete({
    brandId,
    source,
    handle,
  }: BrandIdArg & { source?: string; handle?: string }) {
    const updatedSource = source === 'X' ? 'TWITTER' : source;
    const url = `/brands/${brandId}/competitors/autocomplete`;
    const params = { brandId, source: updatedSource, handle };
    return http.get<AutocompleteResponseBody>(url, { params });
  },

  async createSegmentCluster(
    {
      brandId,
      targetSource,
      targetSourceId,
    }: BrandIdArg & { targetSource: string; targetSourceId: string | number },
    config: HttpRequestConfig = {},
  ) {
    return http.post(
      `/brands/${brandId}/segment_clusters`,
      { targetSource, targetSourceId },
      config,
    );
  },

  async addMediaTag(
    { brandId, mediaId, name }: MediaArg & { name: string },
    config: HttpRequestConfig = {},
  ) {
    const params = { brandId, mediaId };
    const data = { name };

    return http.post<MediaTagResponse>(`/brands/${brandId}/media/${mediaId}/tags`, data, {
      ...config,
      params,
    });
  },

  async addMultipleMediaTags(
    { brandId, mediaIds, tags }: BrandIdArg & { mediaIds: number[]; tags: string[] },
    config: HttpRequestConfig = {},
  ) {
    const params = { brandId };
    const data = { media_ids: mediaIds, tags };

    return http.post(`/brands/${brandId}/media_tags`, data, { ...config, params });
  },

  async removeMediaTag(
    { brandId, mediaId, tagId }: MediaArg & { tagId: number | string },
    config: HttpRequestConfig = {},
  ) {
    const params = { brandId, mediaId, tagId };
    return http.delete(`/brands/${brandId}/media/${mediaId}/tags/${tagId}`, {
      ...config,
      params,
    });
  },

  async updateMediaMeta(
    { brandId, mediaId, meta }: MediaArg & { meta: unknown },
    config: HttpRequestConfig = {},
  ) {
    const params = { brandId, mediaId };
    const data = { meta };

    return http.patch(`/brands/${brandId}/media/${mediaId}`, data, { ...config, params });
  },

  async hideUgc(
    { brandId, mediaIds }: BrandIdArg & { mediaIds: number[] },
    config: HttpRequestConfig = {},
  ) {
    return http.post(`/brands/${brandId}/media/hide_ugc`, { mediaIds }, config);
  },

  async unhideUgc(
    { brandId, mediaIds }: BrandIdArg & { mediaIds: number[] },
    config: HttpRequestConfig = {},
  ) {
    return http.post(`/brands/${brandId}/media/unhide_ugc`, { mediaIds }, config);
  },

  async deleteMediaItem({ brandId, mediaId }: MediaArg, config: HttpRequestConfig = {}) {
    const params = { brandId, mediaId };
    return http.delete(`/brands/${brandId}/media/${mediaId}`, { ...config, params });
  },

  async deleteCampaignMediaItem(
    {
      brandId,
      campaignId,
      mediaIds,
    }: BrandIdArg & { campaignId: number | string; mediaIds: string | number[] },
    config: HttpRequestConfig = {},
  ) {
    return http.delete(`/brands/${brandId}/campaigns/${campaignId}/media?media_ids=${mediaIds}`, {
      ...config,
    });
  },

  async bulkDeleteMedia(
    { brandIds, mediaIds }: { brandIds: number[]; mediaIds: number[] },
    config: HttpRequestConfig = {},
  ) {
    return http.delete('/media/bulk', {
      ...config,
      params: { brandIds: brandIds.join(','), mediaIds: mediaIds.join(',') },
    });
  },

  async getImageDataByMediaId(
    { mediaId }: { mediaId: number | string },
    config: HttpRequestConfig = {},
  ) {
    const params = { mediaId };
    return http.get(`/media/${mediaId}/image_data`, { ...config, params });
  },

  /** @deprecated Can be removed if fetch (imageToBase64) solution works. */
  async getImageDataByUrl({ url }: { url: string }, config: HttpRequestConfig = {}) {
    const params = { url };
    return http.get('/image_data', { ...config, params });
  },

  async updateProduct(
    { brandId, productId, data }: BrandIdArg & { productId: number | string; data: unknown },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/products/${productId}`;
    return http.patch<ProductResponse>(url, data, { ...config });
  },

  async getProducts(
    { brandId, url, ...other }: BrandIdArg & LooseParams & { url?: string },
    config: HttpRequestConfig = {},
  ) {
    let pathName = `/brands/${brandId}/products`;
    let params = merge({}, other);

    if (url) {
      pathName = new URL(url).pathname;
      params = merge(params, parseQuery(url));
    }

    return http.get<ProductsResponse>(pathName, merge({ params }, config));
  },

  async getProduct(
    { brandId, productId }: BrandIdArg & { productId: number | string },
    config: HttpRequestConfig = {},
  ) {
    return http.get<ProductResponse>(`/brands/${brandId}/products/${productId}`, { ...config });
  },

  // Returns a product record with url that matches exactly
  // Also searches product override URLs, returning the associated product record if found
  async findProductByUrl(
    { brandId, url }: BrandIdArg & { url: string },
    config: HttpRequestConfig = {},
  ) {
    return http.post<ProductResponse>(`/brands/${brandId}/products/find_by_url`, { url }, config);
  },

  async addProduct({ brandId }: BrandIdArg, data: unknown, config: HttpRequestConfig = {}) {
    return http.post<ProductResponse>(`/brands/${brandId}/products`, data, { ...config });
  },

  async bulkImportProducts(
    {
      brandId,
      file,
      columnMapping,
    }: BrandIdArg & { file: File; columnMapping: Record<string, string> },
    config: HttpRequestConfig = {},
  ) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('column_mapping', JSON.stringify(columnMapping));
    return http.post(`/brands/${brandId}/products/bulk_imports`, formData, {
      ...config,
      decamelizeRequests: false,
    });
  },

  async updateProductArchivedStatusByIds(
    { brandId, productIds, archived }: BrandIdArg & { productIds: number[]; archived: boolean },
    config: HttpRequestConfig = {},
  ) {
    const data = { product_ids: productIds, archived };
    return http.patch<ArchiveProductsResponse>(`/brands/${brandId}/products/archive`, data, {
      ...config,
    });
  },

  async getProductFeeds(
    { brandId, sort }: BrandIdArg & GetProductFeedsParams,
    config: HttpRequestConfig = {},
  ) {
    const params = { sort };
    return http.get<ProductFeedsResponse>(`/brands/${brandId}/product_feeds`, {
      ...config,
      params,
    });
  },

  async addProductFeed(
    { brandId }: BrandIdArg,
    data: CreateProductFeedBody,
    config: HttpRequestConfig = {},
  ) {
    return http.post<ProductFeedResponse>(`/brands/${brandId}/product_feeds`, data, { ...config });
  },

  async updateProductFeed(
    { brandId, feedId }: BrandIdArg & { feedId: number | string },
    data: CreateProductFeedBody,
    config: HttpRequestConfig = {},
  ) {
    return http.post<ProductFeedResponse>(`/brands/${brandId}/product_feeds/${feedId}`, data, {
      ...config,
    });
  },

  async archiveProductFeedProducts(
    {
      brandId,
      feedId,
      notificationSocketId,
      archived,
    }: BrandIdArg & {
      feedId: number | string;
      notificationSocketId?: string;
      archived: boolean;
    },
    config: HttpRequestConfig = {},
  ) {
    const data: ArchiveProductFeedBody = {
      notification_socket_id: notificationSocketId,
      archived,
    };
    return http.patch<void>(`/brands/${brandId}/product_feeds/${feedId}/products/archive`, data, {
      ...config,
    });
  },

  async deleteProductFeed(
    { brandId, feedId }: BrandIdArg & { feedId: number | string },
    config: HttpRequestConfig = {},
  ) {
    return http.delete<void>(`/brands/${brandId}/product_feeds/${feedId}`, { ...config });
  },

  async getUrlMetadata(
    {
      url,
      id,
      linkNumber,
      retryFetch = false,
    }: { url: string; id?: number | string; linkNumber?: number; retryFetch?: boolean },
    config: HttpRequestConfig = {},
  ) {
    const params = { url, id, linkNumber, retry_fetch: retryFetch };
    return http.get<UrlMetadataResponse>(`/url_metadata`, { ...config, params });
  },

  async getProductsAnalytics(
    {
      brandId,
      scale,
      startDate,
      endDate,
    }: BrandIdArg & { scale?: string; startDate?: string; endDate?: string },
    config: HttpRequestConfig = {},
  ) {
    const params = {
      brandId,
      scale,
      start_date: startDate,
      end_date: endDate,
    };
    return http.get<ProductsAnalyticsResponse>(`/brands/${brandId}/products/click_stats`, {
      ...config,
      params,
    });
  },

  async getProductMedia(
    { brandId, productId }: BrandIdArg & { productId: number | string },
    config: HttpRequestConfig = {},
  ) {
    return http.get<ProductMedia[]>(`/brands/${brandId}/products/${productId}/media`, {
      ...config,
    });
  },

  async getProductClicks(
    {
      brandId,
      productId,
      startDate,
      endDate,
      scale,
      source,
    }: BrandIdArg & {
      productId: number | string;
      startDate?: string;
      endDate?: string;
      scale?: string;
      source?: string;
    },
    config: HttpRequestConfig = {},
  ) {
    const params = { startDate, endDate, scale, source };
    return http.get<ProductClicksResponse>(`/brands/${brandId}/products/${productId}/click_stats`, {
      ...config,
      params,
    });
  },

  async setProductMediaHiddenStatus(
    {
      brandId,
      productId,
      mediaId,
      hideInWidget,
    }: MediaArg & { productId: number | string; hideInWidget: boolean },
    config: HttpRequestConfig = {},
  ) {
    const data = { hide_in_widget: hideInWidget };
    return http.patch<ProductMediaHiddenStatusResponse>(
      `/brands/${brandId}/products/${productId}/media/${mediaId}`,
      data,
      { ...config },
    );
  },

  async getMediaBySourceId(
    {
      brandId,
      sourceId,
      sourceType,
    }: BrandIdArg & { sourceId: number | string; sourceType: string },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/media/source/${sourceId}`;
    const params = { source_type: sourceType };
    return http.get(url, { ...config, params });
  },

  async getCompetitorTagsByBrand(
    { brandIds, includeCompetitors = false }: { brandIds: number[]; includeCompetitors?: boolean },
    config: HttpRequestConfig = {},
  ) {
    const url = '/competitors/tags';
    const data = { brand_ids: brandIds, include_competitors: includeCompetitors };
    return http.put<CompetitorBrandResponse[]>(url, data, config);
  },

  async getUtmSettings(
    { brandId, channel }: BrandIdArg & { channel?: string },
    config?: HttpRequestConfig,
  ) {
    const url = `/brands/${brandId}/utm_settings`;
    return utmHttp.get<UtmSettingsBody>(url, { params: { channel }, ...config });
  },

  async getUtmPresetValues(
    { brandId, channel, trackingKey }: BrandIdArg & GetUtmPresetValuesParams,
    config?: HttpRequestConfig,
  ) {
    const url = `/brands/${brandId}/utm_settings/preset_values`;
    return http.get<UtmPresetValueResponseBody[]>(url, {
      params: { channel, trackingKey },
      ...config,
    });
  },

  async createUtmPresetValue(
    {
      brandId,
      channel,
      trackingKey,
      value,
    }: BrandIdArg & { channel: string; trackingKey: string; value: string },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/utm_settings/preset_values`;
    return http.post<UtmPresetValueResponseBody>(url, { channel, trackingKey, value }, config);
  },

  async updateUtmPresetValue(
    { brandId, id, value }: BrandIdArg & { id: number | string; value: string },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/utm_settings/preset_values/${id}`;
    return http.put<UtmPresetValueResponseBody>(url, { value }, config);
  },

  async deleteUtmPresetValue(
    { brandId, id }: BrandIdArg & { id: number | string },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/utm_settings/preset_values/${id}`;
    return http.delete(url, config);
  },

  async saveUtmSettings(
    { brandId, channel, items }: BrandIdArg & { channel: string; items: unknown },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/utm_settings`;
    const data = {
      [channel]: items,
    };
    return utmHttp.put(url, data, config);
  },

  async decodeUtmSettings(
    { brandId, channel, content, urls }: BrandIdArg & UtmDecodeBody,
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/utm_settings/decode_utms`;
    return utmHttp.post<UtmDecodeResponse>(url, { channel, content, urls }, config);
  },

  async encodeUtmSettings(
    {
      brandId,
      channel,
      shortenUrl,
      customUtms = {},
      mediaId,
      productId,
      content,
      urls,
    }: BrandIdArg & {
      channel: UtmEncodeBody['channel'];
      shortenUrl: boolean;
      customUtms?: UtmEncodeBody['custom_utms'];
      mediaId?: number | string;
      productId?: number | string;
      content: string;
      urls?: string[];
    },
    config: HttpRequestConfig = {},
  ) {
    return http.post<UtmEncodeResponse>(
      `/brands/${brandId}/utm_settings/encode_utms`,
      {
        channel,
        content,
        mediaId,
        customUtms,
        productId,
        shortenUrl,
        urls,
      },
      config,
    );
  },

  async createMediaForManyBrands(data: unknown, config: HttpRequestConfig = {}) {
    return http.post(`/media`, data, { ...config });
  },

  async getAiAlternatives(
    { brandId, mediaId, parentId, limit, offset }: BrandIdArg & GetAiAlternativesParams,
    config: HttpRequestConfig = {},
  ) {
    return http.get<{ data: AiAlternativeMediaResponse[] }>(
      `/brands/${brandId}/ai_alternative_media`,
      {
        ...config,
        params: {
          media_id: mediaId,
          ...(parentId && { parent_id: parentId }),
          ...(limit != null && { limit }),
          ...(offset != null && { offset }),
        },
      },
    );
  },

  async getAiAlternativeById(
    { brandId, alternativeId }: BrandIdArg & { alternativeId: number | string },
    config: HttpRequestConfig = {},
  ) {
    return http.get<AiAlternativeMediaResponse>(
      `/brands/${brandId}/ai_alternative_media/${alternativeId}`,
      config,
    );
  },

  async deleteAiAlternativeMedia(
    brandId: number | string,
    aiAlternativeMediaIds: (number | string)[],
    config: HttpRequestConfig = {},
  ) {
    void brandId;
    return http.delete(`/ai_alternative_media`, {
      ...config,
      data: {
        ai_alternative_media_ids: aiAlternativeMediaIds,
      } as DeleteAiAlternativeMediaBody,
    });
  },

  async getAiAlternativeAds(
    { brandId, sourceAdIds }: BrandIdArg & { sourceAdIds: string[] },
    config: HttpRequestConfig = {},
  ) {
    return http.post<{ data: AiAlternativeAdsResponse[] }>(
      `/brands/${brandId}/ai_alternative_ads`,
      { source_ad_ids: sourceAdIds },
      config,
    );
  },

  async deleteAiAlternativeAds({
    brandId,
    aiAlternativeAdIds,
    config = {},
  }: BrandIdArg & { aiAlternativeAdIds: (number | string)[]; config?: HttpRequestConfig }) {
    return http.delete(`/brands/${brandId}/ai_alternative_ads`, {
      ...config,
      data: {
        ai_alternative_ads_ids: aiAlternativeAdIds,
      } as DeleteAiAlternativeAdsBody,
    });
  },

  async deleteBulkUgcContentRightsFailures(
    { brandId }: BrandIdArg,
    config: HttpRequestConfig = {},
  ) {
    return http.delete(`/brands/${brandId}/bulk_ugc_content_rights_failures`, config);
  },

  getContentRightsCsv(
    { brandId, ...params }: BrandIdArg & CsvParams,
    config: HttpRequestConfig = {},
  ) {
    return http.get<CsvResponse>(
      `/brands/${brandId}/content-rights/csv`,
      merge({ params }, config),
    );
  },
};

export default api;
